/**
 * TrustGraphTab — Trust Center "Trust Ontology" workspace (G-4).
 *
 * The Trust Ontology is the cross-prove substrate: each agent's trust folded from
 * the sealed TrustAssertion stream across all seven proves. It lives here in the
 * Trust Center — not under Identity, which is only one prove.
 *
 * Pick an agent → see its radial Trust Ontology (TrustOntologyView), with the
 * Prove7 score at the hub (from the live trust snapshot) and drill-down per prove.
 */
import React, { useEffect, useState } from 'react';
import { Card } from '../../Card';
import { AgentPickerDropdown } from '../AgentPickerDropdown';
import TrustOntologyView from '../TrustOntologyView';
import { getLiveTrustSnapshot } from '../../../../services/api';

export function TrustGraphTab({ enriched, focusAgentId, onFocusAgent }) {
  const agents = Array.isArray(enriched) ? enriched : [];
  const [agentId, setAgentId] = useState(focusAgentId || null);
  const [agentName, setAgentName] = useState(null);
  const [snap, setSnap] = useState(null);

  // Default to the cross-tab focus (so the selection persists across Trust tabs),
  // else the first scope agent; keep the display name in sync once agents load.
  useEffect(() => {
    if (!agentId && agents.length > 0) {
      const seed = (focusAgentId && agents.find((a) => String(a.id) === String(focusAgentId))) || agents[0];
      setAgentId(String(seed.id));
      setAgentName(seed.name || null);
      return;
    }
    if (agentId && !agentName) {
      const m = agents.find((a) => String(a.id) === String(agentId));
      if (m) setAgentName(m.name || null);
    }
  }, [agents, agentId, agentName, focusAgentId]);

  // Pull the Prove7 score / tier for the hub (honest: null → unrated, no number).
  useEffect(() => {
    if (!agentId) return;
    let alive = true;
    setSnap(null);
    getLiveTrustSnapshot(agentId)
      .then((res) => { if (alive) setSnap(res && res.ok !== false ? (res.data || res) : null); })
      .catch(() => { if (alive) setSnap(null); });
    return () => { alive = false; };
  }, [agentId]);

  const score = snap && typeof snap.score === 'number' ? snap.score : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10.5px] font-mono-v2 tracking-wider text-v2-ink-muted">AGENTIC TRUST INFRASTRUCTURE · TRUST-BASED ACTIONS</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-v2-ink-muted">Subject</span>
          <AgentPickerDropdown
            label={agentName || (agentId ? String(agentId).slice(0, 8) : 'Select agent…')}
            minWidth={220}
            items={agents.map((a) => ({ id: String(a.id), name: a.name, sub: a.archetype || a.type || '' }))}
            onPick={(id, name) => { setAgentId(String(id)); setAgentName(name || null); onFocusAgent?.(String(id)); }}
          />
        </div>
      </div>

      {agentId ? (
        <TrustOntologyView agentId={agentId} agentName={agentName} score={score} tier={snap?.tier} snap={snap} />
      ) : (
        <Card padding="lg">
          <p className="text-[12.5px] text-v2-ink-muted text-center py-6 m-0">
            Select an agent to see its seven-prove Trust Ontology.
          </p>
        </Card>
      )}
    </div>
  );
}

export default TrustGraphTab;
